import { useEffect, useRef } from "react";
import { Field, DateInput } from "./ui";
import { LocationMap } from "./LocationMap";
import { PROPERTY_TYPES, PURPOSES, PROPERTY_STATUS, ENERGY_CLASSES, lookupPostal } from "../../lib/re";
import { toast } from "sonner";

const AMENITIES = [
  ["garage", "Garagem"], ["elevator", "Elevador"], ["balcony", "Varanda"], ["terrace", "Terraço"], ["garden", "Jardim"],
  ["pool", "Piscina"], ["storage_room", "Arrecadação"], ["furnished", "Mobilado"],
];

function useBind(p, set, ro) {
  const text = (k, ph = "", type = "text") => type === "date" ? (
    <DateInput disabled={ro} testId={`prop-${k}`} value={p[k]} onChange={(v) => set({ ...p, [k]: v })} />
  ) : (
    <input type={type} disabled={ro} className="input-almar" placeholder={ph} data-testid={`prop-${k}`}
      value={p[k] ?? ""} onChange={(e) => set({ ...p, [k]: e.target.value })} />
  );
  const sel = (k, opts, ph) => (
    <select disabled={ro} className="input-almar" data-testid={`prop-${k}`} value={p[k] ?? ""} onChange={(e) => set({ ...p, [k]: e.target.value })}>
      {ph && <option value="">{ph}</option>}
      {Object.entries(opts).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
  const area = (k, ph = "") => (
    <textarea disabled={ro} rows={4} className="input-almar" placeholder={ph} data-testid={`prop-${k}`}
      value={p[k] ?? ""} onChange={(e) => set({ ...p, [k]: e.target.value })} />
  );
  return { text, sel, area };
}

export function GeneralTab({ p, set, ro, users, isManager, canPrivate }) {
  const { text, sel, area } = useBind(p, set, ro);
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      <Field label="Título comercial *" span={2}>{text("title", "Ex.: Apartamento T3 com vista rio")}</Field>
      <Field label="Referência"><input disabled className="input-almar font-mono" value={p.reference || "Gerada automaticamente"} data-testid="prop-reference" /></Field>
      <Field label="Tipo de imóvel *">{sel("property_type", PROPERTY_TYPES, "Selecionar")}</Field>
      <Field label="Finalidade *">{sel("purpose", PURPOSES, "Selecionar")}</Field>
      <Field label="Estado">{sel("status", PROPERTY_STATUS)}</Field>
      <Field label="Data de angariação">{text("acquisition_date", "", "date")}</Field>
      <Field label="Consultor responsável">
        <select disabled={ro || !isManager} className="input-almar" data-testid="prop-consultant_id" value={p.consultant_id ?? ""} onChange={(e) => set({ ...p, consultant_id: e.target.value })}>
          <option value="">Eu próprio</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
      </Field>
      <Field label="Origem da angariação">{text("acquisition_source", "Ex.: Referência, portal, prospeção")}</Field>
      <label className="sm:col-span-2 lg:col-span-3 flex items-center gap-3 card-almar px-4 py-3 cursor-pointer">
        <input type="checkbox" disabled={ro} checked={!!p.published} data-testid="prop-published" onChange={(e) => set({ ...p, published: e.target.checked })} />
        <span className="text-sm text-slate-200">Publicar no site<span className="block text-[11px] text-slate-500">Visível no site público apenas se o estado for Disponível, Reservado ou Em negociação. Nunca mostra proprietários, documentos nem observações internas.</span></span>
      </label>
      <Field label="Descrição comercial" span={3}>{area("description", "Texto comercial do imóvel")}</Field>
      <Field label="Título comercial (inglês)" span={3}>{text("title_en", "Usado no site em inglês")}</Field>
      <Field label="Descrição comercial (inglês)" span={3}>{area("description_en", "Usada no site em inglês")}</Field>
      {canPrivate && <Field label="Observações internas confidenciais" span={3} hint="Nunca são publicadas nem visíveis a utilizadores de leitura.">{area("internal_notes")}</Field>}
    </div>
  );
}

export function LocationTab({ p, set, ro }) {
  const { text } = useBind(p, set, ro);
  const last = useRef(p.postal_code);
  useEffect(() => {
    const cp = p.postal_code;
    if (ro || cp === last.current || !/^\d{4}-\d{3}$/.test(cp || "")) return;
    last.current = cp;
    lookupPostal(cp).then((d) => {
      if (!d) { toast.error("Código postal não encontrado"); return; }
      set((cur) => {
        const moved = d.municipality && d.municipality !== cur.municipality;
        const empty = (v) => v === null || v === undefined || v === "";
        return { ...cur, country: cur.country || "Portugal", district: d.district || cur.district,
          municipality: d.municipality || cur.municipality, parish: moved ? (d.parish || "") : (d.parish || cur.parish),
          address: moved ? (d.street || "") : (cur.address || d.street),
          latitude: moved ? (d.latitude ?? "") : (empty(cur.latitude) && d.latitude != null ? d.latitude : cur.latitude),
          longitude: moved ? (d.longitude ?? "") : (empty(cur.longitude) && d.longitude != null ? d.longitude : cur.longitude) };
      });
      toast.success("Morada preenchida a partir do código postal");
    });
  }, [p.postal_code, ro, set]);
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      <Field label="País">{text("country", "Portugal")}</Field>
      <Field label="Distrito">{text("district")}</Field>
      <Field label="Concelho">{text("municipality")}</Field>
      <Field label="Freguesia">{text("parish")}</Field>
      <Field label="Morada completa" span={2}>{text("address")}</Field>
      <Field label="Código postal" hint="Formato 0000-000">{text("postal_code", "0000-000")}</Field>
      <Field label="Latitude">{text("latitude", "", "number")}</Field>
      <Field label="Longitude">{text("longitude", "", "number")}</Field>
      <label className="flex items-center gap-2 text-sm text-slate-300 sm:col-span-2 lg:col-span-3">
        <input type="checkbox" disabled={ro} checked={!!p.show_exact_address} data-testid="prop-show_exact_address" onChange={(e) => set({ ...p, show_exact_address: e.target.checked })} />
        A morada exata pode ser apresentada publicamente
      </label>
      <div className="sm:col-span-2 lg:col-span-3">
        <p className="text-[11px] text-slate-500 mb-2">{ro ? "Localização no mapa" : "Clique no mapa para definir as coordenadas."}</p>
        <LocationMap lat={p.latitude} lng={p.longitude} readOnly={ro} onPick={(lat, lng) => set({ ...p, latitude: lat, longitude: lng })} />
      </div>
    </div>
  );
}

export function FeaturesTab({ p, set, ro }) {
  const { text, area } = useBind(p, set, ro);
  const features = p.custom_features || [];
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Field label="Preço de venda (€)">{text("sale_price", "", "number")}</Field>
        <Field label="Renda mensal (€)">{text("rent_price", "", "number")}</Field>
        <Field label="Área bruta privativa (m²)">{text("area_private", "", "number")}</Field>
        <Field label="Área bruta de construção (m²)">{text("area_construction", "", "number")}</Field>
        <Field label="Área útil (m²)">{text("area_useful", "", "number")}</Field>
        <Field label="Área do terreno (m²)">{text("area_land", "", "number")}</Field>
        <Field label="Quartos">{text("bedrooms", "", "number")}</Field>
        <Field label="Casas de banho">{text("bathrooms", "", "number")}</Field>
        <Field label="Pisos">{text("floors", "", "number")}</Field>
        <Field label="Ano de construção">{text("year_built", "", "number")}</Field>
        <Field label="Estado de conservação">{text("conservation", "Ex.: Bom, Remodelado")}</Field>
        <Field label="Classe energética">
          <select disabled={ro} className="input-almar" data-testid="prop-energy_class" value={p.energy_class ?? ""} onChange={(e) => set({ ...p, energy_class: e.target.value })}>
            <option value="">—</option>{ENERGY_CLASSES.map((c) => <option key={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Validade do certificado energético">{text("energy_cert_expiry", "", "date")}</Field>
        <Field label="Lugares de estacionamento">{text("parking_spaces", "", "number")}</Field>
        <Field label="Exposição solar">{text("sun_exposure", "Ex.: Nascente/Sul")}</Field>
        <Field label="Acessibilidade">{text("accessibility")}</Field>
      </div>
      <div className="flex flex-wrap gap-2">
        {AMENITIES.map(([k, l]) => (
          <button key={k} type="button" disabled={ro} data-testid={`amenity-${k}`} onClick={() => set({ ...p, [k]: !p[k] })}
            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${p[k] ? "bg-[#C9A84C]/20 border-[#C9A84C]/50 text-[#E0C574]" : "border-[#1E3A56] text-slate-400 hover:border-[#C9A84C]/40"}`}>{l}</button>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Equipamentos e acabamentos" span={2}>{area("equipment")}</Field>
        <Field label="Vídeo (URL)">{text("video_url", "https://")}</Field>
        <Field label="Visita virtual (URL)">{text("virtual_tour_url", "https://")}</Field>
        <Field label="Outras características (separadas por vírgula)" span={2}>
          <input disabled={ro} className="input-almar" data-testid="prop-custom_features" value={features.join(", ")}
            onChange={(e) => set({ ...p, custom_features: e.target.value.split(",").map((s) => s.trimStart()).filter((s, i, a) => s || i === a.length - 1) })} />
        </Field>
      </div>
    </div>
  );
}
